/**
 * The toggle that points a rule at elements with no stats yet.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Sits exactly on the identity→matching boundary in the form, because it GATES
 * the block right below it: a no-stats rule has no conditions and no window, so
 * Conditions and Date range disappear while it is on.
 *
 * One per layer, and the UI says so BEFORE the save. The backend refuses a
 * second one, but discovering that only after filling in the whole form is a
 * dead end — so when the slot is taken the switch is disabled and names the
 * rule holding it. The one legal way to have two is to group them (groups are
 * mutually exclusive: highest order wins), which is exactly what re-enables it.
 */

import { FC } from 'react';
import { Sparkles } from 'lucide-react';

import { Switch } from '@/components/ui';

/** The enabled, ungrouped no-stats rule already on this layer, if any. */
export interface NoStatsHolder {
  name: string;
  emoji?: string | null;
}

export interface NoStatsModeCardProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  layer: string;
  /** Set when another rule already holds the slot — the switch is disabled. */
  blockedBy?: NoStatsHolder | null;
}

export const NoStatsModeCard: FC<NoStatsModeCardProps> = ({
  checked,
  onChange,
  layer,
  blockedBy,
}) => (
  <div
    className={`rounded-lg border border-border p-3 ${blockedBy ? 'bg-muted/30' : ''}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <label
          htmlFor="no-stats-mode"
          className="flex items-center gap-1.5 text-sm font-medium"
        >
          <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
          Tag elements with no stats yet
        </label>
        {blockedBy ? (
          <p className="mt-0.5 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              {blockedBy.emoji ? `${blockedBy.emoji} ` : ''}
              {blockedBy.name}
            </span>{' '}
            already tags the {layer} with no stats — only one rule can, or an
            element would wear the same tag twice. Edit that rule instead, or
            give this one a group above to make them mutually exclusive.
          </p>
        ) : (
          <p className="mt-0.5 text-xs text-muted-foreground">
            A brand-new element has no numbers, so it is left out of scoring —
            which also left it out of every rule. Turn this on and the rule tags
            exactly those, and nothing else. It needs no conditions and no date
            range, so both are hidden.
          </p>
        )}
        {checked && !blockedBy && (
          <p className="mt-1.5 text-xs text-warning">
            Pick a tag name no rotation rule already acts on. A rule like
            &quot;remove if tagged X&quot; would start firing on fresh elements
            the moment X becomes a no-stats tag — pausing them before they have
            gathered any data.
          </p>
        )}
      </div>
      <Switch
        id="no-stats-mode"
        checked={checked}
        onCheckedChange={onChange}
        disabled={!!blockedBy}
        aria-label="Tag elements with no stats yet"
      />
    </div>
  </div>
);

export default NoStatsModeCard;
